import { Component, computed, effect, input, output, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormControlState, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { BingAttribute, BingAttributeMetaData, ValueType } from '@vendasta/listing-products';
import { BingAttributeFull, CreateAttribute } from './attribute-interface';

@Component({
  selector: 'forms-bing-attributes',
  templateUrl: './bing-attributes.component.html',
  styleUrls: ['./bing-attributes.component.scss'],
  standalone: false,
})
export class BingAttributesComponent {
  readonly form = input<FormGroup>();
  readonly appearance = input<'outline' | 'standard'>('standard');
  readonly categoryLength = input<number>();
  readonly editDisabled = input<boolean>(false);
  readonly metadata = input.required<BingAttributeMetaData[]>();
  readonly attributes = input.required<BingAttribute[]>();

  readonly navigateToPrimaryInfoTab = output<void>();

  protected readonly orderedInputAttributes = signal<BingAttributeFull[]>([]);
  protected readonly fullAttributes = signal<BingAttributeFull[]>([]);
  protected readonly filteredAttributes: Signal<BingAttributeFull[]>;

  private readonly autoCompleteChanges: Signal<string>;
  protected readonly autocompleteControl = new FormControl('');

  constructor(private fb: FormBuilder) {
    this.autocompleteControl = this.fb.control(this.formStateWithDisabled(''));
    this.autoCompleteChanges = toSignal(this.autocompleteControl.valueChanges);

    effect(() => {
      const fullAttribs: BingAttributeFull[] = [];
      const metadata = this.metadata();

      metadata.forEach((md) => {
        const ba = this.attributes().find((a) => a.name === md.parent);
        const attrib = new BingAttribute({
          name: md.parent,
          value: ba?.value,
        });

        fullAttribs.push(CreateAttribute(attrib, md));
      });

      const sortedAttrs = fullAttribs.sort((a, b) => a.displayName.localeCompare(b.displayName));
      this.fullAttributes.set(sortedAttrs);
    });

    this.filteredAttributes = computed(() => {
      return this.filterValues(this.fullAttributes(), this.autoCompleteChanges()) || [];
    });

    // The metadata will contain all possible attributes the user could set. The attributes will only contain the
    // attributes that the user HAS set. Only the attributes are written to the backend. The metadata + attributes are
    // retrieved from the backend.
    // orderedInputAttributes contains the ordered list of attributes that the user HAS TO/CAN set.
    effect(() => {
      this.fullAttributes().forEach((fa) => {
        this.form().removeControl(fa.name);
        if (!this.attributes().find((a) => a.name === fa.name)) {
          return;
        }

        switch (fa.valueType) {
          case ValueType.REPEATED_ENUM:
            this.form().addControl(
              fa.name,
              this.createCheckboxFormGroup(fa.valueDisplayNames, [], fa.value as string[]),
            );
            break;
          case ValueType.ENUM:
            this.form().addControl(fa.name, this.fb.control(this.formStateWithDisabled(fa.value)));
            break;
          default:
            this.form().addControl(fa.name, this.fb.control(this.formStateWithDisabled(fa.value)));
            break;
        }

        if (this.form().controls[fa.name]) {
          fa.value = this.form().controls[fa.name].value;
        }

        if (fa.value !== null && fa.value !== undefined) {
          this.addToOrderedAttributes(fa);
        }
      });
      this.sortOrderedAttributes();
    });
  }

  autoCompleteDisplayFn(_attributeName: string): string {
    return '';
  }

  async onOptionSelected(event: MatAutocompleteSelectedEvent): Promise<void> {
    const selectedValue = event.option.value;
    const attributes = this.fullAttributes();

    const i = attributes.findIndex((a) => a.name == selectedValue);
    switch (attributes[i].valueType) {
      case ValueType.BOOL:
        attributes[i].value = true;
        break;
      case ValueType.URL:
      case ValueType.ENUM:
        attributes[i].value = '';
        break;
      case ValueType.REPEATED_ENUM:
        attributes[i].value = [];
        break;
      default:
        attributes[i].value = '';
        break;
    }

    this.fullAttributes.set(attributes);
    if (attributes[i].valueType === ValueType.REPEATED_ENUM) {
      this.form().addControl(selectedValue, this.createCheckboxFormGroup(attributes[i].valueDisplayNames, [], []));
    } else {
      this.form().addControl(selectedValue, this.fb.control(this.formStateWithDisabled(attributes[i].value)));
    }

    this.addToOrderedAttributes(attributes[i]);
    this.form().markAsDirty();
  }

  filterValues(values: BingAttributeFull[], search: string) {
    if (search && search.length > 0) {
      return values.filter((attribute) => attribute.displayName.toLowerCase().indexOf(search.toLowerCase()) !== -1);
    } else {
      return values;
    }
  }

  createCheckboxFormGroup(valueDisplayNames: string[], values: string[], setValues: string[]): FormGroup {
    const formGroup = new FormGroup({});
    valueDisplayNames?.forEach((vdn, index) => {
      let value = false;
      setValues?.forEach((sv) => {
        if (values[index] === sv) {
          value = true;
        }
      });
      formGroup.addControl(vdn, this.fb.control(this.formStateWithDisabled(value)));
    });
    return formGroup;
  }

  addToOrderedAttributes(attribute: BingAttributeFull) {
    this.orderedInputAttributes.update((orderedAttributes) => {
      orderedAttributes = orderedAttributes.filter((a) => a.name != attribute.name);
      orderedAttributes.unshift(attribute);
      return orderedAttributes;
    });
  }

  sortOrderedAttributes() {
    this.orderedInputAttributes.update((orderedAttributes) => {
      return orderedAttributes.sort((a, b) => a.displayName.localeCompare(b.displayName));
    });
  }

  removeFromOrderedAttributes(name: string) {
    this.orderedInputAttributes.update((orderedAttributes) => {
      return orderedAttributes.filter((a) => a.name != name);
    });
  }

  private formStateWithDisabled<T>(value?: T): FormControlState<T> {
    return { value: value, disabled: this.editDisabled() };
  }

  async clearAttribute(name) {
    const attributes = this.fullAttributes();

    const i = attributes.findIndex((a) => a.name == name);

    attributes[i].value = null;

    this.removeFromOrderedAttributes(name);

    this.form().removeControl(name);
    this.fullAttributes.set(attributes);
    this.form().markAsDirty();
  }

  showLocationTab() {
    this.navigateToPrimaryInfoTab.emit();
  }
}
